import {
  ArrowButton,
  ButtonsContainer,
  LoadingContainer,
  PageContainer,
  RotatingIcon,
  StatisticsFiltersStyles,
  StyledDismissButton,
  ViewPageContainer
} from 'app/components/common/common-styles';
import { BodyText, FlexColumn, FlexRow } from '@make-software/cspr-design';
import React from 'react';
import { SmoothCollapse } from 'app/components/management/apps/deals/list/smooth-collapse';
import IconArrowDown from 'assets/icons/ic-arrow-down.svg';
import { TaskTypeAdapter, TaskTypeFilter } from 'app/components/filters/task-type-filter';
import { useUrlFilterController } from 'app/components/filters/url-filters';
import {
  EventsGroupBy,
  EventsHistogram,
  EventType,
  EventTypeLabels
} from 'app/console-api/statistics/types';
import { getEventsHistogram } from 'app/console-api/statistics/events-histogram';
import { EntityCountChart } from 'app/components/common/event-chart/entity-count-chart';
import { RequestCountChartHeader } from 'app/components/common/event-chart/chart-header';
import { FailedToFetch } from 'app/components/common/table/table-error';
import { ErrorResult } from 'app/organizations-api/types';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { DealListItemResponse, getMyDeals, getOffers } from 'app/console-api';
import { GraphComponentDataFormatType } from 'app/components/common/charts/graph-component';
import { getPluginsList, Plugins } from 'app/console-api/plugins/plugins';
import { GroupByFilterAdapter } from 'app/components/filters/group-by-filter';
import { Calendar } from 'app/components/common/calendar/calendar';
import { EventTypeFilter, EventTypeFilterAdapter } from 'app/components/filters/event-type-filter';
import { DateRangeFilterAdapter } from 'app/components/filters/date-range-filter';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { ExtendedTasksResponse, getExtendedTasks } from 'app/console-api/tasks/tasks-extended';
import { ExtendedOffersResponse } from 'app/console-api/offers/offers-extended';
import { TaskListFilter, TaskListFilterAdapter } from 'app/components/filters/task-list-filter';
import { OfferListFilter, OfferListFilterAdapter } from 'app/components/filters/offer-list-filter';
import { DealsListFilter, DealsListFilterAdapter } from 'app/components/filters/deals-list-filter';

type LegendItem = {
  id: string; // group id (e.g., appID)
  name: string; // human label (e.g., app name)
  count: number; // total from histogram.totalByGroup
};

export interface GraphDataItemType {
  date: string;

  [key: string]: string | number;
}

export const AppsStatisticsPage = () => {
  const { app } = useTabsCtx();
  const [isOpen, setIsOpen] = React.useState(true);
  const filterController = useUrlFilterController();
  const [histogramData, setHistogramData] = React.useState<EventsHistogram | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<ErrorResult | null>(null);
  const [plugins, setPlugins] = React.useState<Plugins[]>([]);
  const [tasks, setTasks] = React.useState<ExtendedTasksResponse[]>([]);
  const [offers, setOffers] = React.useState<ExtendedOffersResponse[]>([]);
  const [deals, setDeals] = React.useState<DealListItemResponse[]>([]);

  const pluginNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    (plugins ?? []).forEach((p) => m.set(String(p.id), p.name));
    return m;
  }, [plugins]);

  const taskNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    (tasks ?? []).forEach((p) => m.set(String(p.task.id), p.task.name));
    return m;
  }, [tasks]);

  const offerNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    (offers ?? []).forEach((p) => m.set(String(p.offer.id), p.offer.name));
    return m;
  }, [offers]);

  const dealNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    (deals ?? []).forEach((p) => m.set(String(p.dealDetails.id), p.dealDetails.title));
    return m;
  }, [deals]);

  // current groupBy from URL/controller (fallback to event_type)
  const currentGroupBy: EventsGroupBy =
    (filterController.get(GroupByFilterAdapter) as EventsGroupBy) ?? EventsGroupBy.EventType;

  // unified name resolver per group type
  const resolveLegendName = React.useCallback(
    (groupId: string): string => {
      switch (currentGroupBy) {
        case EventsGroupBy.TaskType:
          return pluginNameById.get(groupId) ?? 'Unknown Plugin';
        case EventsGroupBy.EventType:
          return EventTypeLabels[groupId as EventType] ?? groupId;
        case EventsGroupBy.Offer:
          return offerNameById.get(groupId) ?? groupId;
        case EventsGroupBy.Deal:
          return dealNameById.get(groupId) ?? groupId;
        case EventsGroupBy.Task:
          return taskNameById.get(groupId) ?? groupId;
        default:
          return groupId;
      }
    },
    [currentGroupBy, pluginNameById, offerNameById, dealNameById, taskNameById]
  );

  React.useEffect(() => {
    if (!app) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    // fetch both concurrently
    Promise.all([
      getEventsHistogram(filterController, app.id),
      getPluginsList(),
      getExtendedTasks(app.id),
      getOffers(app.id),
      getMyDeals(app.id)
    ])
      .then(([hist, plugins, tasks, offers, deals]) => {
        if (cancelled) return;
        setHistogramData(hist);
        setPlugins(plugins || []);
        setTasks(tasks.data || []);
        setOffers(offers.data || []);
        setDeals(deals.data || []);
      })
      .catch((e) => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [app, filterController]);

  const { series } = React.useMemo(() => {
    if (!histogramData) return { series: [] };

    // Build zero-filled template from every group present in totals
    const allGroupKeys = new Set<string>(
      (histogramData.totalByGroup ?? []).map((x) => String(x.key))
    );

    // rows for the chart: one per bucket with zeros for all groups
    const rows: GraphDataItemType[] = histogramData.buckets.map((b) => {
      const row: GraphDataItemType = { date: b.ts };
      allGroupKeys.forEach((k) => (row[k] = 0));
      (b.groups ?? []).forEach((g) => {
        row[String(g.key)] = g.value;
      });
      return row;
    });

    // legend with counts from totalByGroup; label from apps list (fallback to id)
    const totals = histogramData.totalByGroup ?? [];

    const legend: LegendItem[] = totals
      .map((g) => {
        const id = String(g.key);
        return {
          id,
          name: resolveLegendName(id),
          count: g.value
        };
      })
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    // chart series (one per group)
    const series: GraphComponentDataFormatType[] = legend.map((l) => ({
      graphKey: l.id,
      xAxisValueKey: l.id,
      legendLabel: l.name,
      legendTotal: l.count,
      data: rows,
      initiallySelected: true
    }));

    return { series, legendItems: legend };
  }, [histogramData, pluginNameById, resolveLegendName]);

  const onResetFilters = () => {
    filterController.resetToDefault([
      DateRangeFilterAdapter,
      EventTypeFilterAdapter,
      TaskTypeAdapter,
      GroupByFilterAdapter,
      TaskListFilterAdapter,
      OfferListFilterAdapter,
      DealsListFilterAdapter
    ]);
  };

  return (
    <>
      <PageContainer>
        <ViewPageContainer>
          <FlexRow justify="space-between" gap={8}>
            <BodyText scale={'xl'} size={1} variation="black" noWrap>
              Filters
            </BodyText>
            <ArrowButton
              onClick={() => setIsOpen((v) => !v)}
              aria-expanded={isOpen}
              aria-label={isOpen ? 'Collapse details' : 'Expand details'}
            >
              <RotatingIcon src={IconArrowDown} $open={isOpen} />
            </ArrowButton>
          </FlexRow>

          <SmoothCollapse isOpen={isOpen}>
            <FlexColumn gap={12} style={{ paddingTop: 36 }}>
              <Calendar style={{ maxWidth: 650 }}></Calendar>
              <TaskTypeFilter styles={StatisticsFiltersStyles()}></TaskTypeFilter>
              <TaskListFilter tasks={tasks}></TaskListFilter>
              <OfferListFilter offers={offers}></OfferListFilter>
              <DealsListFilter deals={deals}></DealsListFilter>
              <EventTypeFilter styles={StatisticsFiltersStyles()}></EventTypeFilter>
              <ButtonsContainer gap={10} style={{ maxWidth: 350 }}>
                <StyledDismissButton color={'secondaryBlue'} onClick={onResetFilters}>
                  Reset to default
                </StyledDismissButton>
              </ButtonsContainer>
            </FlexColumn>
          </SmoothCollapse>
        </ViewPageContainer>
      </PageContainer>
      {loading ? (
        <PageContainer>
          <LoadingContainer>
            <SkeletonLoader height={'600px'} />
          </LoadingContainer>
        </PageContainer>
      ) : (
        <EntityCountChart
          chartHeader={
            <RequestCountChartHeader
              title={'Events Count'}
              totalRequests={histogramData?.total || 0}
              optionsList={[
                EventsGroupBy.TaskType,
                EventsGroupBy.Task,
                EventsGroupBy.Offer,
                EventsGroupBy.Deal,
                EventsGroupBy.EventType
              ]}
            />
          }
          yAxisLabelText={'Events count'}
          data={series}
          loading={loading}
          error={error ? <FailedToFetch error={error} /> : null}
        />
      )}
    </>
  );
};
